import api from "../helpers/axios";

/*
Generic per-facility image catalog -- not tied to any one admin-web feature.
Any screen that needs "pick a previously uploaded image or upload a new one,
scoped to a facility" can import this directly. Pass a falsy facilityNumber
(undefined/null) to operate on the global/shared catalog instead of a
specific facility's.
*/
class FacilityImageService {
    async listImages(facilityNumber) {
        try {
            const path = facilityNumber ? `/facility_images/${facilityNumber}` : `/facility_images/global`;
            const response = await api.get(path);
            return response?.data;
        } catch (error) {
            console.error(`Error listing image catalog for facility ${facilityNumber}:`, error);
            throw error;
        }
    }

    // Direct-to-blob upload: ask the backend for a short-lived write URL,
    // PUT the file straight to Azure (bypassing our own API for the actual
    // bytes), then tell the backend the upload succeeded so it can create
    // the catalog record. `fetch` (not the `api` axios instance) is used for
    // the PUT itself -- it targets Azure, not our backend, and must NOT carry
    // our own Authorization header; the SAS token in the URL is what
    // authorizes it.
    async uploadImage(facilityNumber, file) {
        const basePath = facilityNumber ? `/facility_images/${facilityNumber}` : `/facility_images/global`;
        try {
            const { data: uploadInfo } = await api.post(`${basePath}/upload-url`, {
                filename: file.name,
                content_type: file.type,
            });

            const putResponse = await fetch(uploadInfo.upload_url, {
                method: "PUT",
                headers: {
                    "x-ms-blob-type": "BlockBlob",
                    "Content-Type": file.type,
                },
                body: file,
            });
            if (!putResponse.ok) {
                throw new Error(`Direct upload to blob storage failed with status ${putResponse.status}`);
            }

            const { data: registered } = await api.post(`${basePath}/register`, {
                blob_path: uploadInfo.blob_path,
                filename: file.name,
                content_type: file.type,
            });
            return registered;
        } catch (error) {
            console.error(`Error uploading image to catalog for facility ${facilityNumber}:`, error);
            throw error;
        }
    }

    async deleteImage(facilityNumber, imageId) {
        try {
            const path = facilityNumber ? `/facility_images/${facilityNumber}/${imageId}` : `/facility_images/global/${imageId}`;
            await api.delete(path);
        } catch (error) {
            console.error(`Error deleting image ${imageId} for facility ${facilityNumber}:`, error);
            throw error;
        }
    }
}

export default new FacilityImageService();
